import { getBankDetails, submit } from "./helpers";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DirectDebitDialog from "../../../../../../../../Shared/DirectDebitModal";
import DirectDebitLogo from "@assets/img/direct-debit.svg?react";
import Grid2 from "@mui/material/Grid2";
import MemoizedFormInput from "../../../../../../../../Shared/MemoizedFormInput";
import Paper from "@mui/material/Paper";
import React from "react";
import TextField from "@mui/material/TextField";
import bankNames from "@utils/banks";
import { ui } from "../../../../../../../../../interfaces/interfaces";
import { useAppSelector } from "@store/hooks";

const name = "DirectDebitForm";

type Props = {
  prefix: "CarePlanPaymentBankDetails";
  parent: string;
};

const DirectDebitForm: React.FC<Props> = ({ prefix, parent }) => {
  const sortCodeValid = useAppSelector(
    ({ state }) => state.CarePlanPaymentBankDetailsSortCodeValid,
  );
  const accountNumberValid = useAppSelector(
    ({ state }) => state.CarePlanPaymentBankDetailsAccountNumberValid,
  );
  const isSavingJob = useAppSelector(({ state }) => state.SavingJob);
  const sortCode = useAppSelector(
    ({ ui }) => ui.CarePlanPaymentBankDetailsSortCode,
  );
  const accountNumber = useAppSelector(
    ({ ui }) => ui.CarePlanPaymentBankDetailsAccountNumber,
  );
  const serviceAgreementPaymentBankName = useAppSelector(
    ({ ui }) => ui.CarePlanPaymentBankDetailsBankName,
  );
  const serviceAgreementPaymentBankBranch = useAppSelector(
    ({ ui }) => ui.CarePlanPaymentBankDetailsBranchName,
  );

  const [info] = React.useState(
    "Please enter sort code, account number and press validate bank.",
  );
  const [error, setError] = React.useState(false);
  const [bankSucceeded, setBankCheckSucceeded] = React.useState(false);

  const handleChange = (event: any) => {
    const value = event?.target.value ? event.target.value : "";
    const name = event?.target.name;
    setError(false);
    setBankCheckSucceeded(false);
    if (name !== "SortCode" && name !== "AccountNumber") {
      ui.CarePlanPaymentBankDetailsBankName = "";
      ui.CarePlanPaymentBankDetailsBranchName = "";
    }
    const text = (name ? name : "Title") as
      | "SortCode"
      | "AccountNumber"
      | "Title";
    ui[`${prefix}${text}`] = value;
  };

  const handleBankChange = (event: any, value: any) => {
    value = value ? value : event.target.value ? event.target.value : "";
    setError(false);
    setBankCheckSucceeded(false);
    ui[`${prefix}BankName`] = value;
  };

  type A11Props = {
    (name: string): {
      id: string;
      name: string;
      fullWidth: boolean;
      autoComplete: string;
      "aria-controls": string;
      onChange: (event: any) => void;
    };
  };

  const a11yProps: A11Props = (name: string) => {
    return {
      id: `input-${name.toLowerCase()}`,
      name,
      onChange: handleChange,
      fullWidth: true,
      autoComplete: "none",
      "aria-controls": `action-input-${name.toLowerCase()}`,
    };
  };

  return (
    <Grid2 container spacing={2}>
      <Grid2
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <form noValidate>
          <Paper
            elevation={4}
            sx={(theme) => ({
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
              padding: theme.spacing(1),
              [theme.breakpoints.up(600 + Number(theme.spacing(3)) * 2)]: {
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1),
                padding: theme.spacing(1),
              },
            })}
          >
            <Box textAlign="center" fontSize={18} fontWeight="medium">
              Bank Details
            </Box>
            <Grid2 container spacing={2}>
              <MemoizedFormInput
                prefix={prefix}
                parent={"tobeset"}
                inputName="SortCode"
                disabled={false}
                sm={6}
                xs={6}
              />
              <MemoizedFormInput
                prefix={prefix}
                parent={"tobeset"}
                inputName="AccountNumber"
                disabled={false}
                sm={6}
                xs={6}
              />
              <Grid2
                size={{
                  xs: 6,
                  sm: 9,
                }}
              >
                <Autocomplete
                  id="free-solo-demo"
                  onChange={handleBankChange}
                  freeSolo
                  options={bankNames}
                  value={ui[`${prefix}BankName`]}
                  renderInput={(params) => (
                    <TextField
                      variant="outlined"
                      value={serviceAgreementPaymentBankName}
                      {...params}
                      {...a11yProps("BankName")}
                      label="Bank Name:"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={3}>
                <Button
                  disabled={!sortCodeValid || !accountNumberValid}
                  sx={{ lineHeight: 1.4 }}
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    getBankDetails(
                      accountNumber,
                      sortCode,
                      ui,
                      prefix,
                      setBankCheckSucceeded,
                      setError,
                    )
                  }
                >
                  Validate Bank
                </Button>
              </Grid2>
              <Grid2 size={12}>
                {" "}
                <TextField
                  variant="outlined"
                  value={serviceAgreementPaymentBankBranch}
                  required
                  {...a11yProps("BankBranch")}
                  label="Branch Name:"
                  style={{ marginBottom: "15px" }}
                  slotProps={{
                    htmlInput: { readOnly: true },
                  }}
                />
              </Grid2>
            </Grid2>
            <DirectDebitDialog prefix={prefix} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                mb: "-0rem",
                top: "-0rem",
                gap: "1rem",
                width: "fit-content",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  onClick={() => {
                    ui.CarePlanPaymentDirectDebitModal = true;
                  }}
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "fit-content",
                    marginTop: 0,
                    whiteSpace: "normal",
                  }}
                >
                  Guarantee
                </Button>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DirectDebitLogo
                  style={{
                    width: 80,
                    height: "auto",
                    position: "relative",
                    marginBottom: "0rem",
                    top: "0rem",
                  }}
                />
              </Box>
            </Box>
          </Paper>
          <Grid2
            size={{
              xs: 12,
              sm: 12,
            }}
          >
            <Paper elevation={2} sx={{ padding: 2, minWidth: "32em" }}>
              {bankSucceeded ? (
                <Alert severity="success">
                  SUCCESS... {ui[`${prefix}BankName`]} VALIDATED!
                </Alert>
              ) : error ? (
                <Alert severity="error"> {error}</Alert>
              ) : (
                <Alert severity="info">{info}</Alert>
              )}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  disabled={!bankSucceeded || isSavingJob}
                  onClick={submit}
                  variant="contained"
                  color="primary"
                  sx={{
                    width: "fit-content",
                    marginTop: 0,
                    whiteSpace: "normal",
                  }}
                >
                  SELL
                </Button>
              </Box>
            </Paper>
          </Grid2>
        </form>
      </Grid2>
    </Grid2>
  );
};

DirectDebitForm.displayName = name;

export default React.memo(DirectDebitForm);
